import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import rollbarLogger from '../../../../utils/rollbarLogger';
import 'pdfjs-dist/legacy/web/pdf_viewer.css'
import '../styles/PdfRenderer.scss'

let pdfjsLib;
let pdfjsViewer;
(async function () {
  pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
  pdfjsViewer = await import("pdfjs-dist/legacy/web/pdf_viewer.mjs");
  pdfjsLib.GlobalWorkerOptions.workerPort = new Worker(
    new URL("pdfjs-dist/legacy/build/pdf.worker.mjs", import.meta.url),
    {type: "module"}
  );
})();


/**
 * Renders a PDF.
 *
 * @param url the PDF url
 * @param zoom the zoom factor
 * @param rotation the rotation amount in degrees
 * @param onLoad function to call when rendering has finished
 * @return {JSX.Element}
 */
const PdfRenderer = ({
  url,
  zoom = 1.25,
  rotation = 0,
  onLoad = () => {},
}) => {
  const [pdfDocument, setPdf] = useState();

  const [error, setError] = useState("");
  // list of views for us to iterate through and update
  const [views, setViews] = useState([]);

  const eventBus = new pdfjsViewer.EventBus();

  // handles updating a page of the PDF when the rotation/zoom controls are used
  const updatePage = (view) => {
    pdfDocument.getPage(view.id).then(pdfPage => {
      view.scale = zoom;
      view.rotation = rotation;
      view.defaultViewport = pdfPage.getViewport({scale: zoom, rotation: rotation})
      view.setPdfPage(pdfPage);
      view.draw();
    })
  }

  // Handles the initial render for a given page of the PDF
  const renderPage = useCallback(pageNum => {
    pdfDocument.getPage(pageNum).then(pdfPage => {
      const pdfPageView = new pdfjsViewer.PDFPageView({
        container: document.getElementById("pageContainer"),
        id: pageNum,
        scale: zoom,
        rotation: rotation,
        defaultViewport: pdfPage.getViewport({ scale: zoom, rotation: rotation}),
        eventBus
      });
      pdfPageView.setPdfPage(pdfPage);
      views.push(pdfPageView)
      setViews(views)
      return pdfPageView.draw();
    });
  }, [pdfDocument, zoom, rotation]);

  useEffect(() => {
    if (pdfDocument) {
      for (let i = 1; i <= pdfDocument.numPages; i++) {
        if(views.length > 0){
          updatePage(views[i - 1])
        } else {
          renderPage(i);
        }
      }
      onLoad();
    }
  }, [pdfDocument, renderPage]);

  useEffect(() => {
    setPdf(null);
    setViews([]);

    const loadingTask = pdfjsLib.getDocument(url);

    loadingTask.promise
      .then(loadedPdf => setPdf(loadedPdf))
      .catch(err => {
        setError('An error occurred loading the PDF.');
        rollbarLogger.error('Error loading PDF for review', err);
      });
  }, [url]);


  return <React.Fragment>
    {!error.length && pdfDocument && <div style={{position: 'relative'}} id={'pageContainer'}/>}

    {error.length !== 0 && <div className="alert alert-danger">{error}</div>}
  </React.Fragment>;
};

PdfRenderer.propTypes = {
  url: PropTypes.string,
  zoom: PropTypes.number,
  rotation: PropTypes.number,
  onLoad: PropTypes.func,
};

export default PdfRenderer;
